import { useState } from 'react'

import _ from 'lodash'
import qs from 'qs'
import { useParams } from 'react-router-dom'

import {
  LoadingContainer,
  Panels,
  Bar,
  Modal,
  ModelList,
  Pagination,
} from 'peach/components'
import { humanizeKey } from 'peach/helpers'

import { Page } from 'core/components'
import { useQueryString } from 'core/hooks'
import { PickRequired, useLoansAdd, useLoansGet } from 'core/types'
import { LoansGetVariables } from 'core/types/operations'

import {
  CreateInstallmentLoanForm,
  CreateLineOfCreditLoanForm,
} from './CreateLoanForm/CreateLoanForm'
import {
  defaultInstallmentLoan,
  defaultLineOfCreditLoan,
} from './CreateLoanForm/defaultLoan'

const PersonLoans = () => {
  const { personId } = useParams<{
    personId?: string
  }>()

  const [page, setPage] = useState(1)

  const [query, setQuery] = useQueryString<
    PickRequired<
      NonNullable<LoansGetVariables['query']>,
      'limit' | 'startingAfter' | 'endingBefore'
    >
  >({
    limit: 50,
    startingAfter: '',
    endingBefore: '',
  })

  const {
    data: loans,
    pagination: { total: count, nextUrl, previousUrl } = {},
    isLoading,
    error,
  } = useLoansGet({
    personId,
    query,
  })

  const nextLoan = qs.parse(nextUrl ?? '')?.startingAfter
  const previousLoan = qs.parse(previousUrl ?? '')?.endingBefore

  const { mutateAsync: createLoan } = useLoansAdd({
    personId,
  })

  const send = async (loan) =>
    await createLoan({
      ...loan,
      query: { validate: false },
    })

  const groupedLoans = _.groupBy(_.sortBy(loans, 'createdAt').reverse(), 'type')

  return (
    <Panels
      header={
        <Bar
          left={
            <Pagination
              limit={query.limit ?? 0}
              onNext={() => {
                setQuery({
                  ...query,
                  startingAfter: nextLoan,
                  endingBefore: '',
                })
                setPage(page + 1)
              }}
              onPrevious={() => {
                setQuery({
                  ...query,
                  startingAfter: '',
                  endingBefore: page > 2 ? previousLoan : '',
                })
                setPage(page - 1)
              }}
              page={page}
              total={count}
              variant='compact'
            />
          }
          title='All Loans'
        />
      }
    >
      <Modal
        initialValue={defaultInstallmentLoan}
        onSubmit={send}
        render={({ onChange }) => (
          <CreateInstallmentLoanForm onChange={onChange} />
        )}
        tabs
        title='Create Installment Loan'
      />
      <Modal
        initialValue={defaultLineOfCreditLoan}
        onSubmit={send}
        tabs
        title='Create Line Of Credit Loan'
      >
        <CreateLineOfCreditLoanForm />
      </Modal>
      <LoadingContainer error={error} loading={isLoading}>
        <Page variant='full'>
          {_.map(groupedLoans, (group, key) => (
            <ModelList
              columns={[['nickname', 'object']]}
              json={group}
              key={key}
              title={`${humanizeKey(key)} Loans`}
              withDetails={[
                'loanTypeId',
                'type',
                'aprEffectiveAtActivation',
                'apiNominalAtActivation',
                'endDate',
                'productType',
                'servicedBy',
              ]}
              withTimestamps
            />
          ))}
        </Page>
      </LoadingContainer>
    </Panels>
  )
}

export default PersonLoans
